import { Button } from '@mui/material';
import { useNotification } from '@revenuewell/front-end-bundle';
import moment from 'moment';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { ContactApi } from '../../../../../../services/api-client/contact-api';
import { SecureAttachment } from '../../../../../../services/api-client/contact-api.types';
import { NoDataComponent } from '../../../../../shared/grid/no-data';
import { PatientProfileHeader } from '../../header/header';
import { QuickNavMenu } from '../../header/quick-nav-menu/quick-nav-menu';
import { CompleteStatus, DotSeparatedTitle, LiSeparator, List, ListItem } from '../../patient-profile-widget.styles';
import { ViewContent } from '../shared/view-content/view-content';
import { AddFileDialog } from './add-file/add-file';
import { FilesSkeleton } from './files.skeleton';
import { FileDescription, FileInfo, FileName, FilesContent, Toolbar } from './files.styles';

export const FilesView: FC = () => {
  const { showError } = useNotification();
  const { contactDetails, locationId, contactLocation } = usePatientProfile();
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState<SecureAttachment[]>([]);
  const [selectedFile, setSelectedFile] = useState<SecureAttachment>();
  const [fileDialogOpened, setFileDialogOpened] = useState(false);
  const contactApi = useMemo(() => ContactApi.getInstance(), [ContactApi]);

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = useCallback(async () => {
    setIsLoading(true);
    try {
      const files = await contactApi.getSecureAttachments(contactDetails.id, locationId);
      setFiles(files);
    } catch {
      showError('Unable to load patient files.');
    } finally {
      setIsLoading(false);
    }
  }, [contactDetails, locationId]);

  const closeFileDialog = useCallback(() => {
    setFileDialogOpened(false);
    setSelectedFile(undefined);
  }, []);

  const handleFileClick = useCallback((file: SecureAttachment) => {
    setSelectedFile(file);
    setFileDialogOpened(true);
  }, []);

  const handleAddFileClick = useCallback(() => {
    setFileDialogOpened(true);
  }, []);

  const handleFileChange = useCallback(() => {
    loadFiles();
    closeFileDialog();
  }, [loadFiles]);

  return (
    <>
      <PatientProfileHeader />
      <QuickNavMenu />
      <ViewContent>
        <FilesContent>
          {isLoading && <FilesSkeleton />}
          {!isLoading && files.length === 0 && <NoDataComponent />}
          {!isLoading && files.length > 0 && (
            <Scrollbars autoHide style={{ flexGrow: 1 }}>
              <List>
                {files.map((file, index) => {
                  return (
                    <>
                      <ListItem key={file.fileName} onClick={() => handleFileClick(file)}>
                        <FileInfo>
                          <DotSeparatedTitle
                            leftTitle={file.dateCreated ? moment(file.dateCreated).format('MM/DD/YYYY') : null}
                            rightTitle={
                              file.addedByFullName ? <CompleteStatus>{file.addedByFullName}</CompleteStatus> : null
                            }
                          />
                        </FileInfo>
                        <FileName>{file.displayName}</FileName>
                        <FileDescription>{file.description}</FileDescription>
                      </ListItem>
                      {index !== files.length - 1 && <LiSeparator key={1000 + index} />}
                    </>
                  );
                })}
              </List>
            </Scrollbars>
          )}
          {!isLoading && (
            <Toolbar>
              <Button onClick={handleAddFileClick} variant="contained" disableElevation fullWidth>
                Add File
              </Button>
            </Toolbar>
          )}
        </FilesContent>
      </ViewContent>
      <AddFileDialog
        opened={fileDialogOpened}
        attachment={selectedFile}
        patientId={contactDetails.id}
        locationName={contactLocation?.name}
        onClose={closeFileDialog}
        onChange={handleFileChange}
      />
    </>
  );
};
